import React, {useEffect, useState} from 'react';
import {useDispatch, useSelector, useStore} from 'react-redux';
import {useTranslation} from 'react-i18next';
import {useMutation} from '@tanstack/react-query';
import ReSelect from 'react-select';
import {updatePresentationRunFileAction} from '../../../../store/actions/runfile.actions';
import Modal from '../../../../incase-domain/components/modals/directory/Modal';
import {roomSelector} from '../../../../store/selectors/roomRunFile.selectors';
import {usePresentationMode} from '../../../../hooks/usePresentationMode';
import {runFileGroupIdentificationSelector, runFileUserIdSelector} from '../../../../store/selectors/runfile.selectors';
import {loggedInUserSelector} from '../../../../store/selectors/auth.selector';
import {getCssVariable} from '../../../../util/style.utils';
import {patchPresentationRunFile} from '../../../../service/presentation.service';
import {IRunFile} from '../../../../incase-domain/proptypes/runfile';
import {AppState} from '../../../../store/reducer';

function IdentificationModal(): JSX.Element {
    const dispatch = useDispatch();
    const {t} = useTranslation();
    const {mutate} = useMutation({
        mutationFn: ({prev, next}: {prev: IRunFile; next: IRunFile}) => patchPresentationRunFile(prev, next),
    });

    const room = useSelector(roomSelector);
    const runFileUserId = useSelector(runFileUserIdSelector);
    const runFileGroupIdentification = useSelector(runFileGroupIdentificationSelector);
    const loggedInUser = useSelector(loggedInUserSelector);
    const {isPresenter} = usePresentationMode();
    const store = useStore<AppState>();

    const [groupIdentification, setGroupIdentification] = useState<string>('');
    const [modalOpened, setModalOpened] = useState<boolean>(false);

    const isLeaderboardEnabled = room.showLeaderboard;

    const shouldSkipModal = loggedInUser && isLeaderboardEnabled && runFileUserId;

    const isIdentificationSet = !(room.identification?.groupIdentificationOn && !runFileGroupIdentification);

    useEffect(() => {
        if (shouldSkipModal) {
            setModalOpened(false);
            setGroupIdentification('');
            return;
        }

        if (!isIdentificationSet && !isPresenter) {
            setModalOpened(true);
        } else {
            setModalOpened(false);
            setGroupIdentification('');
        }
    }, [room.identification, isIdentificationSet, isPresenter, shouldSkipModal]);

    const handleUpdateIdentification = async (): Promise<void> => {
        const runfile = store.getState().runFileReducer.runFile;
        const updatedRunFile = {...runfile, groupIdentification};
        mutate(
            {prev: runfile, next: updatedRunFile},
            {
                onSuccess: () => {
                    dispatch(updatePresentationRunFileAction(updatedRunFile));
                    setModalOpened(false);
                },
            }
        );
    };

    const isDisabled = room.identification?.groupIdentificationOn && !groupIdentification;

    return (
        <Modal
            isOpen={modalOpened}
            onCancel={() => undefined}
            onConfirm={() => handleUpdateIdentification()}
            confirmDisabled={isDisabled}
            showConfirm
            closeOnClickOutside={false}
        >
            {room.identification?.groupIdentificationOn && (
                <>
                    <label htmlFor="group-identification">
                        {room.identification?.groupIdentificationLabel || t('form.groupIdentificationLabel')}
                    </label>
                    <ReSelect
                        options={room.identification?.options?.map((option) => ({value: option, label: option}))}
                        value={groupIdentification ? {value: groupIdentification, label: groupIdentification} : null}
                        onChange={(option) => setGroupIdentification(option?.value || '')}
                        placeholder={t('slide.elements.multiselectQuestion.selectPlaceholder')}
                        menuPortalTarget={document.body}
                        styles={{menuPortal: (base) => ({...base, zIndex: getCssVariable('--z-index-modal')})}}
                    />
                </>
            )}
        </Modal>
    );
}

export default IdentificationModal;
